import React, { FC, PropsWithChildren, ReactNode } from 'react'
import { default as Wrapper, SkeletonProps as WrapperProps } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

import { List, ListProps, Table, TableColumnsType } from 'antd'

import { withPrefix } from '../../providers/ConfigProvider'
import { VerticalSpace } from '../layout'
import { ListItem, ListItemMeta } from '../list/ListItem'
import { DataGrid } from '../pagination/InfiniteGrid'
import { useResponsive } from '../screen'
import { Title } from '../text/Title'

const Skeleton: FC<PropsWithChildren<WrapperProps>> = ({ style, ...props }) => {
  return (
    <Wrapper width={props.width ?? 100} height={props.height ?? 15} style={{ maxWidth: '100%', ...style }} {...props} />
  )
}

export type SkeletonWrapperProps = {
  loading?: boolean
} & WrapperProps &
  PropsWithChildren

const SkeletonWrapper: FC<PropsWithChildren<SkeletonWrapperProps>> = ({ loading, children, ...props }) => {
  return loading ? <Wrapper width={props.width ?? 100} height={props.height ?? 15} {...props} /> : <>{children}</>
}

const SkeletonTag: FC<{ size?: 'small' | 'middle' | 'large' }> = ({ size = 'middle' }) => {
  return <Wrapper className={withPrefix('skeleton-tag', `skeleton-tag-${size}`)} />
}

const SkeletonAvatar: FC<SkeletonWrapperProps> = ({ children, width, height, ...props }) => {
  return (
    <div
      className={withPrefix('skeleton-avatar-wrapper', 'avatar', 'avatar-circle', 'avatar-image')}
      style={{ width: width ?? '100%', height: height ?? '100%' }}
    >
      <Wrapper {...props} />
    </div>
  )
}

const SkeletonListMeta = () => (
  <ListItemMeta
    avatar={{ placeholder: 'empty', size: 48 }}
    title={<Wrapper height={16} width={50} />}
    description={<Wrapper height={12} width={150} />}
    extra={<Wrapper height={12} width={10} />}
  />
)
type SkeletonListProps = { count: number; bordered: boolean; renderItem: FC }

const SkeletonList: FC<Partial<SkeletonListProps>> = ({
  count = 3,
  bordered = true,
  renderItem: ListItemComponent = SkeletonListMeta,
}) => {
  return (
    <List
      dataSource={new Array(count).fill(0)}
      renderItem={(_, index) => (
        <ListItem bordered={bordered} key={index}>
          <ListItemComponent />
        </ListItem>
      )}
    />
  )
}

type SkeletonTableProps = {
  columns: TableColumnsType<any>
} & Partial<SkeletonListProps>

const SkeletonTable = ({ count = 1, columns }: SkeletonTableProps) => (
  <Table
    dataSource={new Array(count).fill(0).map((_, i) => ({ id: i }))}
    rowKey={'id'}
    columns={columns.map((column) => ({
      ...column,
      render: () => <Wrapper height={24} width={'50%'} />,
    }))}
  />
)

type SkeletonGridProps = {
  count?: number
  skeleton?: ReactNode
  loading?: boolean
  grid?: ListProps<Data.Identified>['grid'] & { gutter: number | [number, number] }
}

const SkeletonGrid: FC<PropsWithChildren<SkeletonGridProps>> = ({ loading = true, children, count: defaultCount }) => {
  const { gridSize } = useResponsive()
  const count = defaultCount ?? gridSize
  const data = new Array<Data.Identified>(count)
    .fill({ id: '' })
    .map((_, index) => ({ id: `${index}`, pk: () => `${index}` }))
    .flat()
  return loading ? (
    <VerticalSpace size={16}>
      <Title level={2}>
        <SkeletonWrapper height={28} />
      </Title>
      <DataGrid data={data} />
    </VerticalSpace>
  ) : (
    children
  )
}

export {
  Skeleton,
  SkeletonAvatar,
  SkeletonListMeta,
  SkeletonTable,
  SkeletonWrapper,
  SkeletonGrid,
  SkeletonTag,
  SkeletonList,
}
